import {
	PlatformDependencies,
	WorkspaceConfiguration,
} from '@citrite/workspace-ui-platform';
import { environment } from 'Environment';
import { clientType, getClientType } from 'Environment/getClientType';
import { isHybridGacsFeatureEnabled } from 'Environment/launchResource/device/platformSpecificFeatureFlags';
import { HTML5Client } from 'Environment/launchResource/html5Client';
import { gacsTicketFetcher } from 'GlobalAppConfigService';
import {
	GacsChromeAppTransport,
	GacsHtml5Transport,
} from 'GlobalAppConfigService/ChromeAppHtml5';
import { wsuiDependencies } from 'GlobalAppConfigService/wsuiDependencies';
import { BrowserExtensionContext } from 'Workspace/BrowserExtension/Context';
import { container } from 'Workspace/DependencyManagement';

export function shouldFetchGacsTokenForClientType(
	currentClientType: clientType,
	workspaceConfig: WorkspaceConfiguration
): boolean {
	return (
		environment.citrixCloudConnected &&
		isHybridGacsFeatureEnabled(workspaceConfig, currentClientType) &&
		gacsTicketFetcher.isGacsV2Enabled(workspaceConfig)
	);
}

/**
 * Starts the client-specific GACS flow if the current client type is eligible
 * for fetching GACS tokens based on the workspace configuration.
 *
 * GACS flow involves the following steps:
 *  1)  checking if GACS feature is enable for the current client type
 *  2)  checking with native client if it needs a new GACS token
 *  3)  if a new GACS token is needed, it will be fetched from the GACS service
 *  4)  The GACS token will be sent to the native client for fetching the GACS Settings
 *
 * @param currentClientType - The type of the current client.
 * @param clientSpecifGacsFlowFunction - The function to execute for the GACS flow.
 */
export function startClientSpecificGacsFlow(
	currentClientType: clientType,
	clientSpecifGacsFlowFunction: () => Promise<void>
): void {
	const workspaceConfig = container
		.resolve(PlatformDependencies.WorkspaceConfiguration)
		.get();

	if (!shouldFetchGacsTokenForClientType(currentClientType, workspaceConfig)) {
		return;
	}

	clientSpecifGacsFlowFunction();
}

/**
 * Returns the client-specific GACS flow function based on the current client type.
 *
 * @param browserExtensionContext - The context for the browser extension.
 * @param html5client - Optional HTML5 client for HTML5 specific flows.
 *                      This function is currently called only at login but we are making it generic for future use.
 *  					hence the html5client is also taken as input.
 * @returns An object containing the current client type and the corresponding GACS flow function.
 */
export function getClientSpecificGacsFlowFunction(
	browserExtensionContext: BrowserExtensionContext,
	html5client?: HTML5Client
) {
	const clientSpecificGacsFlowFunctions = {
		[clientType.browserextension]:
			browserExtensionContext?.validateAndFetchAppConfigurationUsingToken,
		[clientType.chromeapp]:
			GacsChromeAppTransport.getInstance().validateAndFetchAppConfiguration,
		[clientType.html5]: new GacsHtml5Transport(wsuiDependencies, html5client)
			.validateAndFetchAppConfiguration,
	};

	const currentClientType = getClientType();
	const clientSpecifGacsFlowFunction =
		clientSpecificGacsFlowFunctions[
			currentClientType as keyof typeof clientSpecificGacsFlowFunctions
		] || (async () => {});

	return { currentClientType, clientSpecifGacsFlowFunction };
}
